import axios from 'axios';
import MainURL from '../MainURL';

type ApiErrorBody = {
  ok?: boolean;
  error?: { code?: string; message?: string };
  message?: string;
};

export function getPayrollSettingsErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiErrorBody | undefined;
    if (data?.error?.message) return data.error.message;
    if (typeof data?.message === 'string') return data.message;
  }
  return fallback;
}

export type PayrollRecordResult = {
  weeklyHours: number;
  allowanceHours: number;
  monthlyWorkHours: number;
  monthlyAllowanceHours: number;
  monthlyTotalHours: number;
  basePay: number;
  allowancePay: number;
  overtimePay: number;
  grossPay: number;
  pensionAmount: number;
  healthAmount: number;
  longTermCareAmount: number;
  employmentAmount: number;
  freelanceAmount: number;
  totalDeductions: number;
  netPay: number;
};

export type PayrollRecordInput = {
  staffId: number | null;
  employeeName: string;
  department: string | null;
  position: string | null;
  year: number;
  month: number;
  payType: 'hourly' | 'salary';
  hoursMode: 'manual' | 'attendance';
  deductionMode: 'none' | 'insurance' | 'freelance';
  hourlyWage: number;
  monthlySalary: number | null;
  dailyHours: number | null;
  weeklyDays: number | null;
  totalHoursInput: number;
  workDays: number | null;
  overtimeDays: number;
  overtimeHours: number;
  pensionRate: number;
  healthRate: number;
  longTermCareRate: number;
  employmentRate: number;
  freelanceRate: number;
  incomeTax: number;
  etcDeduction: number;
  result: PayrollRecordResult;
};

/** 서버에서 조회되는 급여명세서: POST로 보내는 중첩된 result와 달리 계산 결과가 평평하게 반환된다. */
export type PayrollRecord = {
  id: number;
  staffId: number | null;
  employeeName: string;
  department: string | null;
  position: string | null;
  year: number;
  month: number;
  payType: 'hourly' | 'salary';
  hoursMode: 'manual' | 'attendance';
  deductionMode: 'none' | 'insurance' | 'freelance';
  hourlyWage: number;
  monthlySalary: number | null;
  dailyHours: number | null;
  weeklyDays: number | null;
  totalHoursInput: number;
  workDays: number | null;
  weeklyHours: number;
  allowanceHours: number;
  monthlyWorkHours: number;
  monthlyAllowanceHours: number;
  monthlyTotalHours: number;
  basePay: number;
  allowancePay: number;
  overtimeDays: number;
  overtimeHours: number;
  overtimePay: number;
  grossPay: number;
  pensionRate: number;
  healthRate: number;
  longTermCareRate: number;
  employmentRate: number;
  freelanceRate: number;
  pensionAmount: number;
  healthAmount: number;
  longTermCareAmount: number;
  employmentAmount: number;
  freelanceAmount: number;
  incomeTax: number;
  etcDeduction: number;
  totalDeductions: number;
  netPay: number;
  updatedAt: string;
};

export async function fetchPayrollRecord(
  requesterId: number,
  params: { year: number; month: number; staffId: number | null; employeeName: string },
) {
  const res = await axios.get<{ ok: boolean; data: PayrollRecord | null }>(
    `${MainURL}/api/admin/payroll/records/lookup`,
    {
      params: {
        requesterId,
        year: params.year,
        month: params.month,
        staffId: params.staffId ?? undefined,
        employeeName: params.staffId ? undefined : params.employeeName,
      },
    },
  );
  if (!res.data?.ok) {
    throw new Error('저장된 급여명세서를 불러오지 못했습니다.');
  }
  return res.data.data;
}

export async function savePayrollRecord(requesterId: number, record: PayrollRecordInput) {
  const res = await axios.post<{ ok: boolean; data?: PayrollRecord }>(
    `${MainURL}/api/admin/payroll/records`,
    { requesterId, ...record },
  );
  if (!res.data?.ok || !res.data.data) {
    throw new Error('급여명세서 저장에 실패했습니다.');
  }
  return res.data.data;
}

export async function fetchPayrollRecords(
  requesterId: number,
  filters?: { year?: number; month?: number; staffId?: number; employeeName?: string },
) {
  const res = await axios.get<{ ok: boolean; data: { items: PayrollRecord[]; total: number } }>(
    `${MainURL}/api/admin/payroll/records`,
    { params: { requesterId, ...filters } },
  );
  if (!res.data?.ok) {
    throw new Error('급여명세서 목록을 불러오지 못했습니다.');
  }
  return res.data.data;
}

export async function fetchPayrollRecordDetail(requesterId: number, id: number) {
  const res = await axios.get<{ ok: boolean; data: PayrollRecord | null }>(
    `${MainURL}/api/admin/payroll/records/${id}`,
    { params: { requesterId } },
  );
  if (!res.data?.ok || !res.data.data) {
    throw new Error('급여명세서를 불러오지 못했습니다.');
  }
  return res.data.data;
}
